import { ClientDialog } from '@/components/common/modal/ClientDialog';
import { DialogSingleAction, getDialogContentPaddingBottom } from '@/components/common/modal/DialogSingleAction';
import { PhoneVerificationActions } from '@/components/common/phone/PhoneVerificationActions';
import { PhoneVerificationBody } from '@/components/common/phone/PhoneVerificationBody';
import { SUCCESS_MESSAGE_TITLE_ID, SuccessMessageContent } from '@/components/common/SuccessMessageContent';
import { Theme } from '@mui/material/styles';
import { usePhoneVerificationFlow } from '@/components/common/phone/usePhoneVerificationFlow';
import React, { FC, ReactNode, useEffect } from 'react';
import type { PhoneVerificationFlow } from '@/components/common/phone/usePhoneVerificationFlow';

interface Props {
  open: boolean;
  isAddPhone?: boolean;
  isMobile?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Profile settings dialog for adding or replacing the account's phone number.
 *
 * @param props The dialog state, whether a number is being added or replaced, and the open/close handlers.
 */
export const PhoneNumberDialog: FC<Props> = (props: Props) => {
  const { open, isAddPhone, isMobile, onClose, onOpen } = props;

  const flow: PhoneVerificationFlow = usePhoneVerificationFlow();

  const isSuccess: boolean = flow.step === 'success';

  const handleClose = (): void => {
    if (onClose) {
      onClose();
    }
  };

  const handleBack = (): void => {
    if (flow.goBack()) {
      return;
    }
    handleClose();
  };

  useEffect(() => {
    if (onOpen && open) {
      onOpen();
    }
  }, [onOpen, open]);

  // Keyed on `open` alone. `usePhoneVerificationFlow` hands back a fresh object with fresh closures on every render,
  // so listing `flow` — which is what the lint rule asks for — would run this on every render
  // and reset the flow while the user is still typing into it.
  useEffect(() => {
    if (!open) {
      flow.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const title: string = isAddPhone ? 'Добавить номер телефона' : 'Изменить номер телефона';

  const renderBody = (): ReactNode => {
    if (isSuccess) {
      return (
        <SuccessMessageContent
          title={isAddPhone ? 'Номер телефона привязан' : 'Номер телефона изменён'}
          description={'Номер подтверждён и сохранён в настройках профиля.'}
        />
      );
    }
    return <PhoneVerificationBody flow={flow} isAddPhone={isAddPhone} isMobile={isMobile} />;
  };

  return (
    <ClientDialog
      open={open}
      isMobile={isMobile}
      label={isSuccess ? undefined : title}
      sxTitle={isSuccess ? { paddingBottom: 0 } : undefined}
      aria-labelledby={isSuccess ? SUCCESS_MESSAGE_TITLE_ID : undefined}
      closeLabel={flow.step === 'code' ? 'Назад' : undefined}
      handleCloseButton={handleBack}
      fullWidth={true}
      maxWidth={'xs'}
      PaperProps={{
        sx: {
          minWidth: isMobile ? undefined : 'unset',
          minHeight: isMobile || isSuccess ? '0' : '420px',
        },
      }}
      sxContent={(theme: Theme) => ({
        marginTop: isSuccess ? 0 : theme.spacing(2),
        paddingBottom: getDialogContentPaddingBottom(theme, isMobile, isSuccess),
        overflowY: isSuccess ? undefined : 'visible',
      })}
      actions={
        isSuccess ? (
          <DialogSingleAction label={'Готово'} isMobile={isMobile} onClick={handleClose} />
        ) : (
          <PhoneVerificationActions flow={flow} isMobile={isMobile} onBack={handleBack} />
        )
      }>
      {renderBody()}
    </ClientDialog>
  );
};
